import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import {
  ArrowLeft, Search, Lock, Eye, Sparkles, BookOpen, Zap, Ghost, FileText
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";

export default function EasterEggs() {
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("fragments");

  useEffect(() => {
    api.get("/easter-eggs/collection").then(res => {
      setCollection(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || !collection) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalProgress = (collection.total_progress / collection.total_possible) * 100;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="easter-eggs-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/rewards")} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">EASTER <span className="text-[#F7931A]">EGGS</span></h1>
          <Search className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Progress */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-dark p-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white font-medium">Discovery Progress</span>
            <span className="font-mono text-[#F7931A]">{collection.total_progress}/{collection.total_possible}</span>
          </div>
          <Progress value={totalProgress} className="h-3 mb-3" />
          <div className="flex gap-4 text-xs text-zinc-500">
            <span>Fragments: {collection.fragments_found}/{collection.fragments_total}</span>
            <span>Hidden Eggs: {collection.eggs_found}/{collection.eggs_total}</span>
          </div>
          {collection.satoshi_mode && (
            <div className="mt-4 p-3 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-lg text-center">
              <Sparkles className="text-[#F7931A] mx-auto mb-1" size={20} />
              <p className="text-[#F7931A] font-heading font-bold text-sm">SATOSHI MODE ACTIVATED</p>
            </div>
          )}
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2">
          <button onClick={() => setTab("fragments")}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "fragments" ? "bg-[#F7931A] text-black" : "bg-[#161616] text-zinc-400"}`}>
            Story Fragments ({collection.fragments_found}/21)
          </button>
          <button onClick={() => setTab("hidden")}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === "hidden" ? "bg-[#F7931A] text-black" : "bg-[#161616] text-zinc-400"}`}>
            Hidden Eggs ({collection.eggs_found}/{collection.eggs_total})
          </button>
        </div>

        {/* Story Fragments */}
        {tab === "fragments" && (
          <div className="space-y-2" data-testid="fragments-grid">
            {collection.all_fragments_collected && (
              <div className="card-dark p-4 border-[#F7931A]/30 text-center mb-4">
                <FileText className="text-[#F7931A] mx-auto mb-2" size={24} />
                <p className="text-[#F7931A] font-heading font-bold">ALL 21 FRAGMENTS COLLECTED</p>
                <p className="text-zinc-400 text-xs mt-1">The Bitcoin Brain Story is now assembled.</p>
                <Button onClick={() => navigate("/easter-eggs/story")} className="btn-primary mt-3 text-xs">
                  Read the Full Story
                </Button>
              </div>
            )}
            {collection.fragments.map((frag) => (
              <div key={frag.section_id}
                className={`card-dark p-4 ${frag.found ? "border-[#F7931A]/20" : "opacity-40"}`}
                data-testid={`fragment-${frag.section_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    frag.found ? "bg-[#F7931A]/20" : "bg-zinc-800"
                  }`}>
                    {frag.found ? (
                      <Eye className="text-[#F7931A]" size={16} />
                    ) : (
                      <Lock className="text-zinc-600" size={14} />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${frag.found ? "text-white" : "text-zinc-600"}`}>
                      {frag.title}
                    </p>
                    {frag.found && frag.text && (
                      <p className="text-zinc-400 text-xs mt-2 italic leading-relaxed">
                        "{frag.text.substring(0, 120)}..."
                      </p>
                    )}
                  </div>
                  <span className="text-zinc-600 text-xs">Section {frag.section_id}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hidden Eggs */}
        {tab === "hidden" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="eggs-grid">
            {collection.hidden_eggs.map((egg) => (
              <div key={egg.id}
                className={`card-dark p-4 text-center ${egg.found ? "border-[#F7931A]/20" : "opacity-40"}`}
                data-testid={`egg-${egg.id}`}
              >
                <div className={`w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center ${
                  egg.found ? "bg-[#F7931A]/20 btc-glow" : "bg-zinc-800"
                }`}>
                  {egg.found ? (
                    <Sparkles className="text-[#F7931A]" size={18} />
                  ) : (
                    <Lock className="text-zinc-600" size={14} />
                  )}
                </div>
                <p className={`text-xs ${egg.found ? "text-white" : "text-zinc-600"}`}>
                  {egg.found ? egg.description : "???"}
                </p>
                {egg.found && egg.sats > 0 && (
                  <div className="flex items-center justify-center gap-1 mt-2">
                    <Zap className="text-[#F7931A]" size={10} />
                    <span className="text-[#F7931A] text-xs font-mono">{egg.sats} BTC XP</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
